import React from "react";
import { Info } from "lucide-react";

// Localised labels for the 6 customer-facing categories + row-level lines.
const LABELS = {
  en: {
    heading: "How we calculate this price",
    intro: "Every trip is priced from first principles — vehicle, chauffeur, insurance, energy, platform costs and a fixed operator margin. No surge pricing, no hidden fees.",
    cats: {
      vehicle:    "Vehicle & energy",
      chauffeur:  "Chauffeur labour",
      insurance:  "Insurance allocation",
      trip_fees:  "Airport / parking / tolls",
      platform:   "Platform & software",
      cleaning:   "Cabin cleaning",
      surcharges: "Time-of-day surcharges",
      margin:     "Operator margin",
      extras:     "Extras (child seats · pets)",
    },
    subtotal: "Cost subtotal",
    surcharged: "With time surcharges",
    total: "Total (customer pays)",
    note: "Applied on top of the trip cost. Extras are per-item, without margin.",
  },
  de: {
    heading: "So berechnen wir diesen Preis",
    intro: "Jede Fahrt wird von Grund auf kalkuliert — Fahrzeug, Chauffeur, Versicherung, Energie, Plattform und feste Betreibermarge. Kein Surge-Pricing, keine versteckten Gebühren.",
    cats: {
      vehicle: "Fahrzeug & Energie", chauffeur: "Chauffeur", insurance: "Versicherung",
      trip_fees: "Flughafen / Parkplatz / Maut", platform: "Plattform & Software",
      cleaning: "Fahrzeugreinigung", surcharges: "Zeitzuschläge",
      margin: "Betreibermarge", extras: "Extras (Kindersitze · Haustiere)",
    },
    subtotal: "Kosten-Zwischensumme", surcharged: "Mit Zeitzuschlägen",
    total: "Gesamt (Kunde zahlt)",
    note: "Auf die Fahrtkosten aufgeschlagen. Extras werden pro Stück ohne Marge berechnet.",
  },
  pt: {
    heading: "Como calculamos este preço",
    intro: "Cada viagem é calculada de raiz — veículo, chauffeur, seguro, energia, plataforma e margem fixa. Sem surge pricing, sem taxas ocultas.",
    cats: {
      vehicle: "Veículo & energia", chauffeur: "Chauffeur", insurance: "Seguro",
      trip_fees: "Aeroporto / estacionamento / portagens", platform: "Plataforma & software",
      cleaning: "Limpeza da cabine", surcharges: "Suplementos horários",
      margin: "Margem operacional", extras: "Extras (cadeiras · animais)",
    },
    subtotal: "Subtotal de custos", surcharged: "Com suplementos horários",
    total: "Total (o cliente paga)",
    note: "Aplicado sobre o custo da viagem. Extras são por item, sem margem.",
  },
  fr: {
    heading: "Comment nous calculons ce prix",
    intro: "Chaque trajet est calculé à partir des coûts réels — véhicule, chauffeur, assurance, énergie, plateforme et marge fixe. Pas de surge pricing, pas de frais cachés.",
    cats: {
      vehicle: "Véhicule & énergie", chauffeur: "Chauffeur", insurance: "Assurance",
      trip_fees: "Aéroport / parking / péages", platform: "Plateforme & logiciel",
      cleaning: "Nettoyage cabine", surcharges: "Suppléments horaires",
      margin: "Marge opérateur", extras: "Options (sièges · animaux)",
    },
    subtotal: "Sous-total des coûts", surcharged: "Avec suppléments horaires",
    total: "Total (le client paie)",
    note: "Appliqué au-dessus du coût trajet. Les options sont facturées à l'unité sans marge.",
  },
  it: {
    heading: "Come calcoliamo questo prezzo",
    intro: "Ogni corsa è calcolata dai costi reali — veicolo, chauffeur, assicurazione, energia, piattaforma e margine fisso. Nessun surge pricing, nessuna spesa nascosta.",
    cats: {
      vehicle: "Veicolo & energia", chauffeur: "Chauffeur", insurance: "Assicurazione",
      trip_fees: "Aeroporto / parcheggio / pedaggi", platform: "Piattaforma & software",
      cleaning: "Pulizia cabina", surcharges: "Supplementi orari",
      margin: "Margine operativo", extras: "Extra (seggiolini · animali)",
    },
    subtotal: "Subtotale costi", surcharged: "Con supplementi orari",
    total: "Totale (paga il cliente)",
    note: "Applicato sopra il costo del viaggio. Gli extra sono per articolo, senza margine.",
  },
};

const CATEGORY_ORDER = [
  "vehicle", "chauffeur", "insurance", "trip_fees", "platform", "cleaning", "surcharges", "margin", "extras",
];

/**
 * Renders a transparent, honest breakdown of the trip price.
 * Consumers pass the `breakdown` object exactly as returned by the backend.
 */
export const PricingBreakdown = ({ breakdown, lang = "en" }) => {
  if (!breakdown || !breakdown.categories) return null;
  const t = LABELS[lang] || LABELS.en;
  const c = breakdown.categories;

  const rows = CATEGORY_ORDER
    .filter((k) => (c[k] || 0) > 0.01)
    .map((k) => ({ key: k, label: t.cats[k], value: c[k] }));

  const gold = "#B08D2F";

  return (
    <div className="border border-brand-border bg-brand-surface p-6 md:p-8" data-testid="pricing-breakdown">
      <div className="flex items-start gap-3 mb-6">
        <Info size={16} style={{ color: gold }} className="mt-0.5 shrink-0" />
        <div>
          <p className="text-[10px] tracking-widest2 uppercase" style={{ color: gold }}>— Transparent breakdown</p>
          <h3 className="display text-xl md:text-2xl text-brand-platinum mt-1 leading-tight">{t.heading}</h3>
          <p className="text-brand-silver text-xs md:text-sm mt-3 leading-relaxed max-w-2xl">{t.intro}</p>
        </div>
      </div>

      <div className="border border-brand-border divide-y divide-brand-border">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`bd-row-${r.key}`}>
            <span className="text-brand-silver">{r.label}</span>
            <span className="text-brand-platinum tabular-nums">CHF {r.value.toFixed(2)}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-1 text-xs text-brand-silver">
        <div className="flex justify-between">
          <span>{t.subtotal}</span>
          <span className="tabular-nums">CHF {breakdown.cost_subtotal_chf.toFixed(2)}</span>
        </div>
        {breakdown.categories.surcharges > 0 && (
          <div className="flex justify-between">
            <span>{t.surcharged}</span>
            <span className="tabular-nums">CHF {breakdown.surcharged_subtotal_chf.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-brand-border mt-2 text-brand-platinum text-sm">
          <span>{t.total}</span>
          <span className="tabular-nums font-medium">CHF {breakdown.total_chf.toFixed(2)}</span>
        </div>
      </div>

      <p className="text-[10px] tracking-widest2 uppercase text-brand-dim mt-4">{t.note}</p>
    </div>
  );
};

export default PricingBreakdown;
